import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

import LoginPage from "@/pages/Login";
import RequestDemoPage from "@/pages/RequestDemo";
import DemoCitizen from "@/pages/DemoCitizen";
import DemoLanding from "@/pages/DemoLanding";
import DemoTry from "@/pages/DemoTry";
import ShareRecipientBridge from "@/pages/ShareRecipientBridge";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Identities from "@/pages/Identities";
import AuditLog from "@/pages/AuditLog";
import ApprovalQueue from "@/pages/ApprovalQueue";
import QRPage from "@/pages/QRPage";
import Notifications from "@/pages/Notifications";
import Users from "@/pages/Users";
import Institutions from "@/pages/Institutions";
import IdentityDetail from "@/pages/IdentityDetail";
import Recovery from "@/pages/Recovery";
import RecoveryDetail from "@/pages/RecoveryDetail";
import Developer from "@/pages/Developer";
import Analytics from "@/pages/Analytics";
import LiveAudit from "@/pages/LiveAudit";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import InstitutionDetail from "@/pages/InstitutionDetail";
import Digest from "@/pages/Digest";
import Onboarding from "@/pages/Onboarding";
import EmailOps from "@/pages/EmailOps";
import Security from "@/pages/Security";
import Settings from "@/pages/Settings";
import DesignSystem from "@/pages/DesignSystem";
import AppShell from "@/components/AppShell";
import AdminShell from "@/portals/AdminShell";
import InstitutionShell from "@/portals/InstitutionShell";
import DemoShell from "@/portals/DemoShell";
import DeveloperShell from "@/portals/DeveloperShell";
import { InternalOnlyRoute, TenantRoute, DeveloperRoute, DemoRoute } from "@/portals/PortalGuards";
import { portalsForUser } from "@/portals/portalRouting";
import { Loader2 } from "lucide-react";
import MarketingLayout from "@/marketing/MarketingLayout";
import VerifyEmailPage from "@/marketing/VerifyEmailPage";
import ResetPasswordPage from "@/marketing/ResetPasswordPage";
import {
  Home as MHome,
  Product as MProduct,
  HowItWorks as MHow,
  ForCitizens as MCitizen,
  ForInstitutions as MInst,
  Developers as MDev,
  TrustSecurity as MTrust,
  Security as MSec,
  Company as MCompany,
  About as MAbout,
  Contact as MContact,
  FAQ as MFaq,
  RequestDemo as MReq,
  Privacy as MPriv,
  Terms as MTerms,
  Cookies as MCookies,
  SecurityLegal as MSecLegal,
  Support as MSupport,
  Hospitality as MHosp,
  EnterpriseServices as MEnt,
  Health as MHealth,
  Finance as MFin,
  Government as MGov,
  Leadership as MLead,
  Engage as MEngage,
  Resources as MRes,
  Status as MStatus,
  SecurityReport as MSecRep,
} from "@/marketing/pages";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <AppShell>{children}</AppShell>;
}

// Portal routes provide their own shell (sidebar + header); do not wrap in AppShell.
// Each portal is guarded by its specific role gate (InternalOnlyRoute,
// TenantRoute, DeveloperRoute, DemoRoute) so an authenticated citizen who
// stumbles onto /admin sees a Forbidden screen, not the internal shell.
// Historical bug (P0 remediated 2026-02-XX Session N-Security): this guard
// previously checked only `!user`, allowing any authenticated session to
// render `AdminShell` — surfacing the admin sidebar (KYC Reviews, System
// Settings, Wallet Portraits, etc.) even though the data XHRs 401/403'd
// server-side. Split now per-route: `/admin/*` → InternalOnlyRoute,
// `/institution/*` → TenantRoute, `/developer/*` → DeveloperRoute.
function PortalRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  // Hostname-aware entry (2026-08 ecosystem reconciliation): the SPA
  // is served from a single bundle at every `*.veralok.com` host, but
  // portal-specific subdomains must land on their portal root rather
  // than the public marketing home. Path-based deep links (/product,
  // /legal/*, /login, /reset-password, /verify-email, etc.) remain
  // untouched — this only affects the bare `/` entry.
  const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
  const HOST_TO_PORTAL = {
    "institution.veralok.com": "/institution",
    "admin.veralok.com":       "/admin",
    "developer.veralok.com":   "/developer",
  };
  // Demo v2 hostname routing (2026-02-21): on demo.veralok.com, an
  // unauthenticated visitor lands on the public /request-demo form,
  // and an authenticated evaluator lands in the Institution portal
  // (backed by the isolated Demo v2 tenancy on the demo backend).
  if (host.toLowerCase() === "demo.veralok.com") {
    return <Navigate to={user ? "/institution" : "/request-demo"} replace />;
  }
  const portalPath = HOST_TO_PORTAL[host.toLowerCase()];
  if (portalPath) {
    return <Navigate to={portalPath} replace />;
  }
  // Authenticated users on the marketing hosts → their portal.
  // Unauthenticated → public marketing home.
  if (user) {
    const { primary } = portalsForUser(user);
    return <Navigate to={primary} replace />;
  }
  return <MarketingLayout><MHome /></MarketingLayout>;
}

// Legacy paths remain reachable (per V1.0 policy) but authenticated users
// are transparently redirected to their portal-scoped equivalent so that a
// stale bookmark to /dashboard, /institutions, etc. keeps working through
// the transition. Unauthenticated visitors hit /login as usual via
// ProtectedRoute below.
function LegacyRedirect() {
  const { user } = useAuth();
  const { primary } = portalsForUser(user);
  return <Navigate to={primary} replace />;
}

function RouterInner() {
  const location = useLocation();
  // Handle OAuth callback synchronously before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* /request-demo is a PUBLIC marketing surface on every host and
          serves the marketing MReq form → POST /api/marketing/demo-request.
          Historical bug (RLP #1): a duplicate top-level route mapped
          `/request-demo` to `<RequestDemoPage />` (the demo-portal page
          that POSTs to /api/demo/request — an endpoint only registered
          in demo runtime). Because React Router matches top-to-bottom,
          the demo-portal page shadowed the marketing form on
          veralok.com and every submission returned HTTP 405 Method Not
          Allowed. The single marketing route below (line ~225) is now
          the sole /request-demo mapping.

          On demo.veralok.com, the RootRedirect still points an
          unauthenticated visitor at /request-demo — they land on the
          marketing form, which submits to the demo backend's copy of
          /api/marketing/demo-request (registered in both runtimes). */}
      {/* Public verify-email landing — token IS the credential; no auth required.
          Cohort testers tap the link in their welcome email and land here
          instead of on the raw JSON API response. See LCM O-17. */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* Public reset-password landing — token IS the credential; no
          auth required. Pairs with EMAIL_APP_BASE_URL pointing at the
          SPA host (api.veralok.com in prod), so the reset link the
          citizen taps actually resolves to a real page. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Demo v2 · unauthenticated Demo Citizen consent surface.
          Rendered publicly on demo.veralok.com so the evaluator can
          complete the full institution → citizen consent loop in the
          Demo trial. The backend endpoints
          `/api/demo/citizen/consent/:id` and
          `/api/demo/citizen/decide/:id` are demo-mode-only, so this
          page has no equivalent authority on the production API. */}
      {/* Demo v2 · Lane 1 public showcase (only on demo.veralok.com) */}
      <Route path="/demo" element={<DemoLanding />} />
      <Route path="/demo/try" element={<DemoTry />} />
      <Route path="/demo-citizen/:consent_id" element={<DemoCitizen />} />
      {/* Public Share Pass recipient — production ALB routes /share/{id}
          directly to FastAPI SSR; this SPA bridge only fires in preview. */}
      <Route path="/share/:id" element={<ShareRecipientBridge />} />
      {/* Legacy dashboard path — redirect authenticated users to their portal. */}
      <Route path="/dashboard" element={<PortalRoute><LegacyRedirect /></PortalRoute>} />
      <Route path="/identities" element={<InternalOnlyRoute><Identities /></InternalOnlyRoute>} />
      <Route path="/identities/:vlk_id" element={<InternalOnlyRoute><IdentityDetail /></InternalOnlyRoute>} />
      <Route path="/recovery" element={<InternalOnlyRoute><Recovery /></InternalOnlyRoute>} />
      <Route path="/recovery/:request_id" element={<InternalOnlyRoute><RecoveryDetail /></InternalOnlyRoute>} />
      {/* Legacy /developer path — redirect authenticated callers to the
          dedicated developer portal shell (/developer/*). Session N. */}
      <Route path="/developer" element={<PortalRoute><Navigate to="/developer/" replace /></PortalRoute>} />
      <Route path="/analytics" element={<InternalOnlyRoute><Analytics /></InternalOnlyRoute>} />
      {/* /institutions and /security are marketing pages for the public.
          Authenticated users on these paths are redirected to their portal. */}
      <Route path="/institutions/:vlk_inst_id" element={<InternalOnlyRoute><InstitutionDetail /></InternalOnlyRoute>} />
      <Route path="/approvals" element={<InternalOnlyRoute><ApprovalQueue /></InternalOnlyRoute>} />
      <Route path="/audit" element={<InternalOnlyRoute><AuditLog /></InternalOnlyRoute>} />
      <Route path="/audit/live" element={<InternalOnlyRoute><LiveAudit /></InternalOnlyRoute>} />
      <Route path="/cases" element={<InternalOnlyRoute><Cases /></InternalOnlyRoute>} />
      <Route path="/cases/:case_id" element={<InternalOnlyRoute><CaseDetail /></InternalOnlyRoute>} />
      <Route path="/digest" element={<InternalOnlyRoute><Digest /></InternalOnlyRoute>} />
      <Route path="/onboarding" element={<InternalOnlyRoute><Onboarding /></InternalOnlyRoute>} />
      <Route path="/email" element={<InternalOnlyRoute><EmailOps /></InternalOnlyRoute>} />
      <Route path="/qr" element={<InternalOnlyRoute><QRPage /></InternalOnlyRoute>} />
      <Route path="/notifications" element={<InternalOnlyRoute><Notifications /></InternalOnlyRoute>} />
      <Route path="/users" element={<InternalOnlyRoute><Users /></InternalOnlyRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/design-system" element={<ProtectedRoute><DesignSystem /></ProtectedRoute>} />
      {/* Iteration 21C-C portal split — role/tenant-guarded shells (own layout).
         SECURITY (Session N-Security P0 remediation 2026-02-XX): each portal
         MUST use its role-specific guard. The generic `PortalRoute` (below)
         only enforces authentication and is intentionally reserved for
         legacy /developer, /dashboard redirects. Prior to this change,
         /admin/* used `PortalRoute` which allowed any authenticated citizen
         to render `AdminShell` (backend data XHRs still 401/403'd but the
         admin sidebar was visible). */}
      <Route path="/admin/*" element={<InternalOnlyRoute><AdminShell /></InternalOnlyRoute>} />
      <Route path="/institution/*" element={<TenantRoute><InstitutionShell /></TenantRoute>} />
      <Route path="/demo-portal/*" element={<DemoRoute><DemoShell /></DemoRoute>} />
      <Route path="/developer/*" element={<DeveloperRoute><DeveloperShell /></DeveloperRoute>} />

      {/* Public marketing site — unauthenticated. Authenticated users at `/`
          are redirected to their portal (see RootRedirect). */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/product"       element={<MarketingLayout><MProduct /></MarketingLayout>} />
      <Route path="/how-it-works"  element={<MarketingLayout><MHow /></MarketingLayout>} />
      <Route path="/citizen"       element={<MarketingLayout><MCitizen /></MarketingLayout>} />
      <Route path="/institutions"  element={<MarketingLayout><MInst /></MarketingLayout>} />
      {/* VeraLok Public Presence — five Super Institution gateways. */}
      <Route path="/hospitality"          element={<MarketingLayout><MHosp /></MarketingLayout>} />
      <Route path="/enterprise-services"  element={<MarketingLayout><MEnt /></MarketingLayout>} />
      <Route path="/health"               element={<MarketingLayout><MHealth /></MarketingLayout>} />
      <Route path="/finance"              element={<MarketingLayout><MFin /></MarketingLayout>} />
      <Route path="/government"           element={<MarketingLayout><MGov /></MarketingLayout>} />
      <Route path="/developers"    element={<MarketingLayout><MDev /></MarketingLayout>} />
      <Route path="/trust-security" element={<MarketingLayout><MTrust /></MarketingLayout>} />
      {/* /security kept as a legacy alias — points to the same TrustSecurity page. */}
      <Route path="/security"      element={<MarketingLayout><MSec /></MarketingLayout>} />
      <Route path="/company"       element={<MarketingLayout><MCompany /></MarketingLayout>} />
      {/* /about kept as a legacy alias for the renamed Company page. */}
      <Route path="/about"         element={<MarketingLayout><MAbout /></MarketingLayout>} />
      <Route path="/leadership"    element={<MarketingLayout><MLead /></MarketingLayout>} />
      <Route path="/engage"        element={<MarketingLayout><MEngage /></MarketingLayout>} />
      <Route path="/resources"     element={<MarketingLayout><MRes /></MarketingLayout>} />
      <Route path="/status"        element={<MarketingLayout><MStatus /></MarketingLayout>} />
      <Route path="/security-report" element={<MarketingLayout><MSecRep /></MarketingLayout>} />
      <Route path="/contact"       element={<MarketingLayout><MContact /></MarketingLayout>} />
      <Route path="/faq"           element={<MarketingLayout><MFaq /></MarketingLayout>} />
      <Route path="/request-demo"  element={<RequestDemoPage />} />
      <Route path="/legal/privacy" element={<MarketingLayout><MPriv /></MarketingLayout>} />
      <Route path="/legal/terms"   element={<MarketingLayout><MTerms /></MarketingLayout>} />
      <Route path="/legal/cookies" element={<MarketingLayout><MCookies /></MarketingLayout>} />
      <Route path="/legal/security" element={<MarketingLayout><MSecLegal /></MarketingLayout>} />
      {/* Public Support page — target of the App Store Connect Support URL (Apple Guideline 1.5). */}
      <Route path="/support"       element={<MarketingLayout><MSupport /></MarketingLayout>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <RouterInner />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "hsl(var(--card))",
                  color: "hsl(var(--card-foreground))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 2,
                  fontFamily: "'Manrope', system-ui, sans-serif",
                },
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
